import type { AppNotification } from "types/Notification";
import type { BudgetTransaction } from "types/BudgetTransaction";
import type { GroupSummary } from "types/Group";
import type { InstrumentEvent } from "features/market";
import type { QuoteGuidePanelData } from "features/market";
import { findImportantMarketMove, IMPORTANT_MARKET_MOVE_CONFIG } from "features/market";
import type { QuoteCurrency } from "features/market";
import type { Operation, PositionWithAsset } from "types/Portfolio";
import {
    buildCardStatementDueNotifications,
    buildGroupActivityNotifications,
    buildUpcomingPersonalExpenseNotifications,
} from "lib/notifications";
import { rankInstrumentEvent, type PersonalUpcomingPaymentRanking } from "lib/guideRanking";
import {
    getActionableInstrumentEvents,
    type PositionInstrumentEvent,
} from "features/market";
import { findTopSpendingInsight } from "lib/spendingInsights";
import { getCategoryLabel } from "constants/categories";
/** Path after host: includes language prefix, e.g. /en/john/personal?view=calendar */
export function buildPersonalGuidePath(language: string, username: string, viewCalendar = true): string {
    const base = `/${language}/${encodeURIComponent(username)}/personal`;
    return viewCalendar ? `${base}?view=calendar` : base;
}

export function buildPersonalCalendarDayPath(
    language: string,
    username: string,
    dueDate: Date,
): string {
    const yyyy = dueDate.getFullYear();
    const mm = String(dueDate.getMonth() + 1).padStart(2, "0");
    const dd = String(dueDate.getDate()).padStart(2, "0");
    return `/${language}/${encodeURIComponent(username)}/personal?view=calendar&day=${yyyy}-${mm}-${dd}`;
}

export function buildGroupGuidePath(language: string, username: string, groupName: string): string {
    return `/${language}/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`;
}

export function buildInvestmentsGuidePath(language: string, username: string): string {
    return `/${language}/${encodeURIComponent(username)}/investments`;
}

export function buildCotizacionesGuidePath(language: string, username: string): string {
    return `/${language}/${encodeURIComponent(username)}/cotizaciones`;
}

export function buildAppDeepLink(pathWithLeadingSlash: string): string {
    const p = pathWithLeadingSlash.startsWith("/") ? pathWithLeadingSlash.slice(1) : pathWithLeadingSlash;
    return `sihayplata://${p}`;
}

export function buildPersonalGuideItems(params: {
    transactions: BudgetTransaction[];
    balancesVisible: boolean;
    locale: string;
    language: string;
    username: string;
    max?: number;
    now?: Date;
    ranking?: PersonalUpcomingPaymentRanking;
    t?: (key: string, options?: Record<string, unknown>) => string;
}): AppNotification[] {
    const cardDueBase = buildCardStatementDueNotifications({
        transactions: params.transactions,
        balancesVisible: params.balancesVisible,
        locale: params.locale,
        max: params.max,
        now: params.now,
        ranking: params.ranking,
    });
    const cardDue = cardDueBase.map((n, i) => {
        const dueDate = n.date ? new Date(n.date) : new Date();
        const path = buildPersonalCalendarDayPath(params.language, params.username, dueDate);
        return enrichPersonal(n, path, i, 1100);
    });

    const base = buildUpcomingPersonalExpenseNotifications({
        transactions: params.transactions,
        balancesVisible: params.balancesVisible,
        locale: params.locale,
        max: params.max,
        now: params.now,
        ranking: params.ranking,
    });
    const path = buildPersonalGuidePath(params.language, params.username);
    const upcoming = base.map((n, i) => enrichPersonal(n, path, i));

    const insightItems = params.t
        ? buildCategorizationGuideItems({
            transactions: params.transactions,
            language: params.language,
            username: params.username,
            t: params.t,
            now: params.now,
        })
        : [];

    return [...insightItems, ...cardDue, ...upcoming];
}

export function buildCategorizationGuideItems(params: {
    transactions: BudgetTransaction[];
    language: string;
    username: string;
    t: (key: string, options?: Record<string, unknown>) => string;
    now?: Date;
}): AppNotification[] {
    const now = params.now ?? new Date();
    const path = buildPersonalGuidePath(params.language, params.username, false);
    const items: AppNotification[] = [];

    const insight = findTopSpendingInsight(params.transactions, now);
    if (insight) {
        const categoryLabel = getCategoryLabel(insight.category, params.t);
        items.push({
            id: `personal-spending-insight-${insight.category}-${insight.merchant}`,
            scope: "personal",
            tone: "warning",
            title: params.t("categorization.spendingInsightTitle", { category: categoryLabel }),
            body: params.t("categorization.spendingInsightBody", {
                category: categoryLabel,
                merchant: insight.merchant,
                percent: insight.increasePercent,
                share: Math.round(insight.merchantShareOfCategory * 100),
            }),
            actionLabel: params.t("categorization.spendingInsightAction"),
            date: now.toISOString(),
            category: "personal",
            dedupeKey: `personal:spending:${insight.category}:${insight.merchant}`,
            deepLink: path,
            dismissible: true,
            surface: ["guide"],
            priority: 900,
        });
    }

    return items;
}

function enrichPersonal(item: AppNotification, path: string, index: number, priorityBase = 1000): AppNotification {
    const source = item.sourceId ?? item.id;
    return {
        ...item,
        category: "personal",
        dedupeKey: `personal:${source}`,
        deepLink: path,
        dismissible: true,
        surface: ["guide", "push"],
        priority: priorityBase - index,
    };
}

export function buildGroupGuideItems(params: {
    groups: GroupSummary[];
    username: string;
    userId?: string;
    balancesVisible: boolean;
    locale: string;
    language: string;
    max?: number;
    groupId?: string;
    now?: Date;
}): AppNotification[] {
    const base = buildGroupActivityNotifications({
        groups: params.groups,
        username: params.username,
        userId: params.userId,
        balancesVisible: params.balancesVisible,
        locale: params.locale,
        max: params.max,
        groupId: params.groupId,
        now: params.now,
    });
    return base.map((n, i) => enrichGroup(n, params.groups, params.language, params.username, i));
}

function enrichGroup(
    item: AppNotification,
    groups: GroupSummary[],
    language: string,
    username: string,
    index: number,
): AppNotification {
    const parts = item.id.split("-");
    const groupId = parts[0];
    const group = groups.find((g) => g._id === groupId);
    const path = group ? buildGroupGuidePath(language, username, group.name) : `/${language}/${encodeURIComponent(username)}/groups`;
    const txId = item.sourceId ?? "";
    return {
        ...item,
        category: "groups",
        dedupeKey: groupId && txId ? `group:${groupId}:${txId}` : `group:${item.id}`,
        deepLink: path,
        dismissible: true,
        surface: ["guide", "push"],
        priority: 1000 - index,
        meta: item.meta,
    };
}

export function buildPortfolioGuideItems({
    positions,
    operations,
    instrumentEvents,
    totalValue,
    quoteCurrency,
    getPositionValue,
    t,
    language,
    username,
}: {
    positions: PositionWithAsset[];
    operations: Operation[];
    instrumentEvents: InstrumentEvent[];
    totalValue: number;
    quoteCurrency: string;
    getPositionValue?: (position: PositionWithAsset) => number;
    t: (key: string, options?: Record<string, unknown>) => string;
    language: string;
    username: string;
}): AppNotification[] {
    if (positions.length === 0) return [];

    const actionable = getActionableInstrumentEvents({
        positions,
        operations,
        events: instrumentEvents,
        upcomingDays: 45,
    });

    const instrumentGuideItem = buildInstrumentGuideItem({
        actionableInstrumentEvent: actionable[0],
        t,
        language,
        username,
    });
    if (instrumentGuideItem) return [instrumentGuideItem];

    const topPosition = [...positions]
        .map((position) => ({
            position,
            value: getPositionValue?.(position) ?? position.quantity * (position.currentPrice ?? position.avgPrice),
        }))
        .sort((a, b) => b.value - a.value)[0];
    const share = totalValue > 0 && topPosition ? topPosition.value / totalValue : 0;
    if (topPosition && share >= 0.4) {
        const symbol = topPosition.position.assetId?.symbol ?? topPosition.position.assetId?.name ?? t("portfolio.position");
        const path = buildInvestmentsGuidePath(language, username);
        return [{
            id: "portfolio-concentration",
            scope: "personal",
            tone: "info",
            title: t("portfolio.concentrationTitle", { symbol }),
            body: t("portfolio.concentrationBody", {
                percent: `${Math.round(share * 100)}%`,
                currency: quoteCurrency,
            }),
            date: new Date().toISOString(),
            category: "investments",
            dedupeKey: "investments:concentration",
            deepLink: path,
            dismissible: true,
            surface: ["guide"],
            priority: 100,
        }];
    }

    return [];
}

export function buildInstrumentGuideItem({
    actionableInstrumentEvent,
    t,
    language,
    username,
}: {
    actionableInstrumentEvent?: PositionInstrumentEvent;
    t: (key: string, options?: Record<string, unknown>) => string;
    language: string;
    username: string;
}): AppNotification | null {
    if (!actionableInstrumentEvent) return null;
    const { event, position, daysUntil, status } = actionableInstrumentEvent;
    const symbol = position.assetId?.symbol ?? event.ticker;
    const total = event.total != null ? `${event.total}` : "-";
    const date = event.date;
    const path = buildInvestmentsGuidePath(language, username);
    const priority = rankInstrumentEvent(actionableInstrumentEvent);

    if (status === "overdue") {
        return {
            id: `portfolio-${event.id}`,
            scope: "personal",
            tone: "warning",
            title: t("portfolio.instrumentEventOverdueTitle", { symbol }),
            body: t("portfolio.instrumentEventOverdueBody", { date, total, currency: event.currency }),
            meta: t("portfolio.instrumentEventEstimatedSource"),
            actionLabel: t("portfolio.recordAmortization"),
            sourceId: `instrument-event:${event.id}`,
            date: new Date().toISOString(),
            category: "investments",
            dedupeKey: `investments:event:${event.id}`,
            deepLink: path,
            dismissible: true,
            surface: ["guide", "push"],
            priority,
        };
    }

    return {
        id: `portfolio-${event.id}`,
        scope: "personal",
        tone: status === "dueToday" ? "warning" : "info",
        title: status === "dueToday"
            ? t("portfolio.instrumentEventTodayTitle", { symbol })
            : t("portfolio.instrumentEventUpcomingTitle", { symbol, days: daysUntil }),
        body: t("portfolio.instrumentEventUpcomingBody", { date, total, currency: event.currency }),
        meta: t("portfolio.instrumentEventEstimatedSource"),
        actionLabel: status === "dueToday" ? t("portfolio.recordAmortization") : undefined,
        sourceId: `instrument-event:${event.id}`,
        date: new Date().toISOString(),
        category: "investments",
        dedupeKey: `investments:event:${event.id}`,
        deepLink: path,
        dismissible: true,
        surface: ["guide", "push"],
        priority,
    };
}

function formatGuideVariation(value: number): string {
    return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function formatCompactVolume(value: number): string {
    return new Intl.NumberFormat("es-AR", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(value);
}

export function buildQuoteGuideItems(params: {
    panels: QuoteGuidePanelData[];
    quoteCurrency: QuoteCurrency;
    language: string;
    username: string;
    t: (key: string, options?: Record<string, unknown>) => string;
}): AppNotification[] {
    const move = findImportantMarketMove(params.panels, params.quoteCurrency, IMPORTANT_MARKET_MOVE_CONFIG);
    if (!move) return [];
    const panelLabel = params.t(`investments.${move.panel}`);
    const path = buildCotizacionesGuidePath(params.language, params.username);
    return [{
        id: `quotes-aggressive-move-${move.panel}-${move.symbol}`,
        scope: "personal",
        tone: move.variation >= 0 ? "success" : "warning",
        title: params.t(
            move.variation >= 0
                ? "investments.aggressiveMoveUpTitle"
                : "investments.aggressiveMoveDownTitle",
            { symbol: move.symbol },
        ),
        body: params.t("investments.aggressiveMoveBody", {
            name: move.name,
            variation: formatGuideVariation(move.variation),
        }),
        meta: move.volume != null
            ? params.t("investments.aggressiveMoveMeta", {
                panel: panelLabel,
                volume: formatCompactVolume(move.volume),
            })
            : panelLabel,
        actionLabel: params.t("investments.viewPanel", { panel: panelLabel }),
        sourceId: `quote-panel:${move.panel}`,
        date: new Date().toISOString(),
        category: "quotes",
        dedupeKey: `quotes:${move.panel}:${move.symbol}`,
        deepLink: path,
        dismissible: true,
        surface: ["guide"],
        priority: Math.abs(move.variation) * 10,
    }];
}

export function buildManualFxQuoteGuideItem(params: {
    language: string;
    username: string;
    t: (key: string, options?: Record<string, unknown>) => string;
}): AppNotification {
    const path = buildCotizacionesGuidePath(params.language, params.username);
    return {
        id: "quotes-manual-conversion",
        scope: "personal",
        tone: "info",
        title: params.t("investments.manualFxGuideTitle"),
        body: params.t("investments.manualFxGuideBody"),
        date: new Date().toISOString(),
        category: "quotes",
        dedupeKey: "quotes:manual-fx",
        deepLink: path,
        dismissible: true,
        surface: ["guide"],
        priority: 1,
    };
}
